import React, { useState, useEffect, useCallback } from "react";
import { Bell, Mail, CheckCircle, Loader2, Info, AlertCircle, Copy, ExternalLink, X, Send, Unlink } from "lucide-react";
import axios from "axios";
import Cookies from "js-cookie";
import Sidebar from "@/components/Sidebar";
import { getClientAuthHeaders, getClientAuthToken } from "@/lib/apiAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Telegram icon component (matches sidebar)
const TelegramIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

// Preference event type labels
const PREFERENCE_LABELS = {
  new_lead: { label: "New Lead", description: "When a new lead reaches out to your Digital Brain" },
  meeting_booked: { label: "Meeting Booked", description: "When a meeting is successfully scheduled" },
  meeting_rescheduled: { label: "Meeting Rescheduled", description: "When a meeting is moved to a new time" },
  meeting_canceled: { label: "Meeting Canceled", description: "When a scheduled meeting is canceled" },
  human_handoff_requested: { label: "Human Handoff Requested", description: "When the Digital Brain transfers a conversation to a human agent" },
  system_errors: { label: "System Errors", description: "Critical system issues that need attention" },
};

// Default preferences — all unchecked when user has no saved settings
const DEFAULT_PREFERENCES = {
  new_lead: { email: false, telegram: false },
  meeting_booked: { email: false, telegram: false },
  meeting_rescheduled: { email: false, telegram: false },
  meeting_canceled: { email: false, telegram: false },
  human_handoff_requested: { email: false, telegram: false },
  system_errors: { email: false, telegram: false },
};

// Toggle switch component
const ToggleSwitch = ({ enabled, onChange, disabled = false, testId }) => (
  <button
    type="button"
    role="switch"
    aria-checked={enabled}
    disabled={disabled}
    onClick={() => onChange(!enabled)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#00D1FF]/50 focus:ring-offset-2 focus:ring-offset-[#0F1115] ${
      disabled ? "opacity-50 cursor-not-allowed" : ""
    } ${enabled ? "bg-[#00D1FF]" : "bg-white/10"}`}
    data-testid={testId}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
        enabled ? "translate-x-5" : "translate-x-0"
      }`}
    />
  </button>
);

const Notifications = () => {
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);
  const [emailAddress, setEmailAddress] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [apiError, setApiError] = useState("");
  const [emailError, setEmailError] = useState("");

  // Telegram connection state
  const [telegramConnected, setTelegramConnected] = useState(false);
  const [telegramUsername, setTelegramUsername] = useState("");
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [activationCode, setActivationCode] = useState("");
  const [codeCopied, setCodeCopied] = useState(false);

  // Test Connection & Disconnect state
  const [showTestModal, setShowTestModal] = useState(false);
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testMessageResult, setTestMessageResult] = useState(null); // "success" | "error" | null
  const [testMessageError, setTestMessageError] = useState("");
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  // Extracted fetchSettings so it can be called after disconnect
  const fetchSettings = useCallback(async () => {
    setIsLoading(true);
    const token = getClientAuthToken();
    const userEmail = Cookies.get("email") || "";

    if (!token) {
      setEmailAddress(userEmail);
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/notifications/settings`, {
        headers: getClientAuthHeaders(),
      });

      const data = response.data;

      if (data.has_settings && data.settings) {
        const saved = data.settings;

        const merged = { ...DEFAULT_PREFERENCES };
        if (saved.preferences) {
          Object.keys(merged).forEach((key) => {
            if (saved.preferences[key]) {
              merged[key] = {
                email: !!saved.preferences[key].email,
                telegram: !!saved.preferences[key].telegram,
              };
            }
          });
        }
        setPreferences(merged);

        setEmailAddress(saved.channels?.email_address || userEmail);

        const tg = saved.channels?.telegram;
        if (
          tg &&
          tg.chat_id &&
          tg.status === "active" &&
          tg.bot_blocked === false
        ) {
          setTelegramConnected(true);
          setTelegramUsername(tg.username || "");
        } else {
          setTelegramConnected(false);
          setTelegramUsername("");
        }
      } else {
        setPreferences(DEFAULT_PREFERENCES);
        setEmailAddress(data.default_email || userEmail);
        setTelegramConnected(false);
        setTelegramUsername("");
      }
    } catch (err) {
      console.error("Failed to fetch notification settings:", err);
      setEmailAddress(userEmail);
    }

    setIsLoading(false);
  }, []);

  // Fetch saved notification settings on mount
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Email validation
  const validateEmail = (email) => {
    if (!email) return "Email address is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return "Please enter a valid email address";
    return "";
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmailAddress(value);
    setSaveSuccess(false);
    setApiError("");
    if (emailError) {
      setEmailError(validateEmail(value));
    }
  };

  const handleEmailBlur = () => {
    setEmailError(validateEmail(emailAddress));
  };

  // Handle Connect Telegram button click
  const handleConnectTelegram = async () => {
    setIsGeneratingCode(true);
    setApiError("");
    try {
      const token = getClientAuthToken();
    if (!token) {
        setApiError("You are not logged in. Please login and try again.");
        setIsGeneratingCode(false);
        return;
      }
      const response = await axios.post(
        `${BACKEND_URL}/api/notifications/telegram/generate_activation_code`,
        {},
        { headers: getClientAuthHeaders() }
      );
      setActivationCode(response.data.activation_code);
      setShowTelegramModal(true);
    } catch (err) {
      console.error("Failed to generate activation code:", err);
      setApiError(
        err.response?.data?.detail || "Failed to generate activation code. Please try again."
      );
    }
    setIsGeneratingCode(false);
  };

  // Copy activation code to clipboard
  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(activationCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2500);
    } catch {
      // Fallback for older browsers
      const textarea = document.createElement("textarea");
      textarea.value = activationCode;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2500);
    }
  };

  // Toggle a specific preference
  const togglePreference = (eventType, channel) => {
    setSaveSuccess(false);
    setApiError("");
    setPreferences((prev) => ({
      ...prev,
      [eventType]: {
        ...prev[eventType],
        [channel]: !prev[eventType][channel],
      },
    }));
  };

  // Handle Test Connection — open the test modal
  const handleOpenTestModal = () => {
    setTestMessageResult(null);
    setTestMessageError("");
    setShowTestModal(true);
  };

  // Handle Send Test Message
  const handleSendTestMessage = async () => {
    setIsSendingTest(true);
    setTestMessageResult(null);
    setTestMessageError("");
    try {
      const token = getClientAuthToken();
    if (!token) {
        setTestMessageResult("error");
        setTestMessageError("You are not logged in. Please login and try again.");
        setIsSendingTest(false);
        return;
      }
      await axios.post(
        `${BACKEND_URL}/api/notifications/telegram/send_test_message`,
        {},
        { headers: getClientAuthHeaders() }
      );
      setTestMessageResult("success");
    } catch (err) {
      console.error("Failed to send test message:", err);
      setTestMessageResult("error");
      setTestMessageError(
        err.response?.data?.detail || "Failed to send test message. Please try again."
      );
    }
    setIsSendingTest(false);
  };

  // Handle Disconnect Telegram
  const handleDisconnectTelegram = async () => {
    setIsDisconnecting(true);
    try {
      const token = getClientAuthToken();
    if (!token) {
        setApiError("You are not logged in. Please login and try again.");
        setIsDisconnecting(false);
        return;
      }
      await axios.post(
        `${BACKEND_URL}/api/notifications/telegram/disconnect`,
        {},
        { headers: getClientAuthHeaders() }
      );
      // Refetch settings to update UI
      await fetchSettings();
      setShowDisconnectModal(false);
    } catch (err) {
      console.error("Failed to disconnect Telegram:", err);
      setApiError(
        err.response?.data?.detail || "Failed to disconnect Telegram. Please try again."
      );
    }
    setIsDisconnecting(false);
  };

  // Handle save — POST to backend
  const handleSave = async () => {
    const error = validateEmail(emailAddress);
    if (error) {
      setEmailError(error);
      return;
    }

    setIsSaving(true);
    setSaveSuccess(false);
    setApiError("");

    try {
      const token = getClientAuthToken();
    if (!token) {
        setApiError("You are not logged in. Please login and try again.");
        setIsSaving(false);
        return;
      }

      await axios.post(
        `${BACKEND_URL}/api/notifications/settings`,
        {
          preferences,
          channels: {
            email_address: emailAddress,
            telegram: {},
          },
        },
        {
          headers: getClientAuthHeaders(),
        }
      );

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 4000);
    } catch (err) {
      console.error("Failed to save notification settings:", err);
      if (err.response) {
        setApiError(
          err.response.data?.detail || err.response.data?.message || "Failed to save settings. Please try again."
        );
      } else {
        setApiError("Network error. Please check your connection and try again.");
      }
    }

    setIsSaving(false);
  };

  return (
    <div className="min-h-screen bg-[#0F1115] flex" data-testid="notifications-page">
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content */}
      <div className="flex-1 md:ml-64 flex flex-col">
        <main className="flex-1 px-6 py-10">
          <div className="w-full max-w-3xl mx-auto animate-fade-in-up">
            {/* Page Title */}
            <div className="mb-8">
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-[#F3F4F6] mb-2 flex items-center gap-3">
                <Bell className="w-7 h-7 text-[#00D1FF]" />
                Notifications
              </h1>
              <p className="text-[#9CA3AF] font-body text-sm">
                Choose how and when you want to be notified about important events
              </p>
            </div>

            {/* Loading state */}
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 text-[#00D1FF] animate-spin" />
              </div>
            ) : (
              <>
                {/* Success Message */}
                {saveSuccess && (
                  <div className="mb-6 p-4 rounded-lg bg-green-500/10 border border-green-500/30" data-testid="save-success-message">
                    <p className="text-green-400 text-sm font-body text-center flex items-center justify-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Notification settings saved successfully!
                    </p>
                  </div>
                )}

                {/* API Error */}
                {apiError && (
                  <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30" data-testid="api-error">
                    <p className="text-red-400 text-sm font-body text-center flex items-center justify-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      {apiError}
                    </p>
                  </div>
                )}

                {/* ===== Notification Preferences Card ===== */}
                <div className="glass rounded-2xl p-6 md:p-8 border border-white/10 mb-6">
                  <h2 className="font-heading font-bold text-lg md:text-xl text-[#F3F4F6] mb-1">
                    Notification Preferences
                  </h2>
                  <p className="text-[#9CA3AF] font-body text-sm mb-6">
                    Toggle notifications for each event type and delivery channel
                  </p>

                  {/* Header row */}
                  <div className="hidden sm:grid sm:grid-cols-[1fr_80px_80px] gap-4 items-center mb-4 px-4">
                    <span className="text-[#9CA3AF] text-xs font-body uppercase tracking-wider">Event</span>
                    <span className="text-[#9CA3AF] text-xs font-body uppercase tracking-wider text-center flex items-center justify-center gap-1">
                      <Mail className="w-3.5 h-3.5" /> Email
                    </span>
                    <span className="text-[#9CA3AF] text-xs font-body uppercase tracking-wider text-center flex items-center justify-center gap-1">
                      <TelegramIcon className="w-3.5 h-3.5" /> Telegram
                    </span>
                  </div>

                  {/* Preference rows */}
                  <div className="space-y-1">
                    {Object.entries(PREFERENCE_LABELS).map(([key, { label, description }]) => (
                      <div
                        key={key}
                        className="grid grid-cols-1 sm:grid-cols-[1fr_80px_80px] gap-3 sm:gap-4 items-center px-4 py-3 rounded-lg hover:bg-white/[0.02] transition-colors"
                        data-testid={`preference-row-${key}`}
                      >
                        {/* Event info */}
                        <div>
                          <p className="text-[#F3F4F6] font-body text-sm font-medium">{label}</p>
                          <p className="text-[#9CA3AF] font-body text-xs mt-0.5">{description}</p>
                        </div>

                        {/* Mobile labels + toggles */}
                        <div className="flex sm:hidden items-center gap-6 mt-1">
                          <div className="flex items-center gap-2">
                            <Mail className="w-3.5 h-3.5 text-[#9CA3AF]" />
                            <span className="text-[#9CA3AF] text-xs">Email</span>
                            <ToggleSwitch
                              enabled={preferences[key].email}
                              onChange={() => togglePreference(key, "email")}
                              testId={`toggle-${key}-email`}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <TelegramIcon className="w-3.5 h-3.5 text-[#9CA3AF]" />
                            <span className="text-[#9CA3AF] text-xs">Telegram</span>
                            <ToggleSwitch
                              enabled={preferences[key].telegram}
                              onChange={() => togglePreference(key, "telegram")}
                              testId={`toggle-${key}-telegram`}
                            />
                          </div>
                        </div>

                        {/* Desktop toggles */}
                        <div className="hidden sm:flex justify-center">
                          <ToggleSwitch
                            enabled={preferences[key].email}
                            onChange={() => togglePreference(key, "email")}
                            testId={`toggle-${key}-email`}
                          />
                        </div>
                        <div className="hidden sm:flex justify-center">
                          <ToggleSwitch
                            enabled={preferences[key].telegram}
                            onChange={() => togglePreference(key, "telegram")}
                            testId={`toggle-${key}-telegram`}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ===== Delivery Channels Card ===== */}
                <div className="glass rounded-2xl p-6 md:p-8 border border-white/10 mb-6">
                  <h2 className="font-heading font-bold text-lg md:text-xl text-[#F3F4F6] mb-1">
                    Delivery Channels
                  </h2>
                  <p className="text-[#9CA3AF] font-body text-sm mb-6">
                    Configure where your notifications are delivered
                  </p>

                  {/* Email Address */}
                  <div className="mb-8">
                    <label htmlFor="notification-email" className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body mb-2">
                      <Mail className="w-4 h-4 text-[#00D1FF]" />
                      Email Address
                    </label>
                    <input
                      id="notification-email"
                      type="email"
                      value={emailAddress}
                      onChange={handleEmailChange}
                      onBlur={handleEmailBlur}
                      placeholder="your@email.com"
                      className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-[#F3F4F6] placeholder-[#9CA3AF]/50 focus:outline-none transition-all font-body ${
                        emailError
                          ? "border-red-500/70 focus:border-red-500 focus:ring-1 focus:ring-red-500/50"
                          : "border-white/10 focus:border-[#00D1FF]/50 focus:ring-1 focus:ring-[#00D1FF]/50"
                      }`}
                      data-testid="notification-email-input"
                    />
                    {emailError && (
                      <p className="mt-1.5 text-red-400 text-xs font-body" data-testid="email-error">
                        {emailError}
                      </p>
                    )}
                    <p className="mt-1.5 text-[#9CA3AF]/60 text-xs font-body">
                      Notifications will be sent to this email address
                    </p>
                  </div>

                  {/* Telegram Channel */}
                  <div>
                    <div className="flex items-center gap-2 text-[#F3F4F6] text-sm font-body mb-2">
                      <TelegramIcon className="w-4 h-4 text-[#229ED9]" />
                      Telegram
                    </div>

                    {telegramConnected ? (
                      /* ---- Telegram connected ---- */
                      <div className="rounded-lg border border-green-500/30 bg-green-500/5 p-4" data-testid="telegram-connected-card">
                        <div className="flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                          <div>
                            <p className="text-green-400 font-body text-sm font-medium">
                              Telegram notifications are successfully connected
                            </p>
                            {telegramUsername && (
                              <p className="text-[#9CA3AF] font-body text-xs mt-0.5">
                                Connected as {telegramUsername}
                              </p>
                            )}
                          </div>
                        </div>
                        {/* Action buttons */}
                        <div className="flex items-center gap-3 mt-4 pt-3 border-t border-green-500/10">
                          <button
                            onClick={handleOpenTestModal}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-body font-medium transition-colors duration-200 bg-white/10 text-[#F3F4F6] hover:bg-white/15 border border-white/10"
                            data-testid="test-connection-button"
                          >
                            <Send className="w-4 h-4" />
                            Test Connection
                          </button>
                          <button
                            onClick={() => setShowDisconnectModal(true)}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-body font-medium transition-colors duration-200 bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20"
                            data-testid="disconnect-telegram-button"
                          >
                            <Unlink className="w-4 h-4" />
                            Disconnect
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* ---- Telegram not connected ---- */
                      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="telegram-channel-card">
                        <p className="text-[#9CA3AF] font-body text-sm mb-3">
                          Connect Telegram to receive notifications
                        </p>
                        <button
                          onClick={handleConnectTelegram}
                          disabled={isGeneratingCode}
                          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-body font-medium transition-colors duration-200 bg-[#229ED9] text-white hover:bg-[#229ED9]/90 disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="connect-telegram-button"
                        >
                          {isGeneratingCode ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              Generating code...
                            </>
                          ) : (
                            <>
                              <TelegramIcon className="w-4 h-4" />
                              Connect Telegram
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                  <button
                    onClick={handleSave}
                    disabled={isSaving || !!emailError}
                    className={`font-heading font-bold text-base py-3 px-8 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2 ${
                      !isSaving && !emailError
                        ? "bg-[#00D1FF] text-[#0F1115] hover:bg-[#00D1FF]/90"
                        : "bg-[#00D1FF]/50 text-[#0F1115]/70 cursor-not-allowed"
                    }`}
                    data-testid="save-notifications-button"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      "Save Settings"
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </main>

        {/* Telegram Activation Code Modal */}
        {showTelegramModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4" data-testid="telegram-modal-overlay">
            <div className="bg-[#1A1D23] rounded-2xl border border-white/10 p-6 md:p-8 w-full max-w-md shadow-2xl relative animate-fade-in-up" data-testid="telegram-modal">
              {/* Close button */}
              <button
                onClick={async () => { await fetchSettings(); setShowTelegramModal(false); }}
                className="absolute top-4 right-4 text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors"
                data-testid="telegram-modal-close"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#229ED9]/10 flex items-center justify-center flex-shrink-0">
                  <TelegramIcon className="w-5 h-5 text-[#229ED9]" />
                </div>
                <h3 className="font-heading font-bold text-lg text-[#F3F4F6]">
                  Connect Telegram Notifications
                </h3>
              </div>

              {/* Instructions */}
              <div className="space-y-3 mb-6">
                <p className="text-[#9CA3AF] font-body text-sm">
                  To connect Telegram notifications, follow these steps:
                </p>
                <ol className="list-decimal list-inside text-[#9CA3AF] font-body text-sm space-y-2">
                  <li>
                    Open the bot{" "}
                    <a
                      href="https://t.me/FractoLabsNotificationsBot"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#229ED9] hover:underline inline-flex items-center gap-1"
                    >
                      @FractoLabsNotificationsBot
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </li>
                  <li>Send the activation code below to the bot</li>
                  <li>Done! You will start receiving notifications</li>
                </ol>
              </div>

              {/* Activation Code */}
              <div className="rounded-lg border border-white/10 bg-white/5 p-4 mb-6">
                <p className="text-[#9CA3AF] text-xs font-body mb-2">Your activation code:</p>
                <div className="flex items-center gap-3">
                  <code className="flex-1 text-[#F3F4F6] text-xl font-mono tracking-widest font-bold text-center select-all" data-testid="activation-code">
                    {activationCode}
                  </code>
                  <button
                    onClick={handleCopyCode}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-body font-medium transition-all duration-200 ${
                      codeCopied
                        ? "bg-green-500/20 text-green-400 border border-green-500/30"
                        : "bg-white/10 text-[#F3F4F6] hover:bg-white/15 border border-white/10"
                    }`}
                    data-testid="copy-code-button"
                  >
                    {codeCopied ? (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Open Bot Button */}
              <a
                href="https://t.me/FractoLabsNotificationsBot"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-body font-bold transition-colors duration-200 bg-[#229ED9] text-white hover:bg-[#229ED9]/90"
                data-testid="open-bot-button"
              >
                <TelegramIcon className="w-4 h-4" />
                Open @FractoLabsNotificationsBot
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        )}

        {/* Test Connection Modal */}
        {showTestModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4" data-testid="test-connection-modal-overlay">
            <div className="bg-[#1A1D23] rounded-2xl border border-white/10 p-6 md:p-8 w-full max-w-md shadow-2xl relative animate-fade-in-up" data-testid="test-connection-modal">
              {/* Close button */}
              <button
                onClick={() => setShowTestModal(false)}
                className="absolute top-4 right-4 text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors"
                data-testid="test-modal-close"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#229ED9]/10 flex items-center justify-center flex-shrink-0">
                  <Send className="w-5 h-5 text-[#229ED9]" />
                </div>
                <h3 className="font-heading font-bold text-lg text-[#F3F4F6]">
                  Test Telegram Connection
                </h3>
              </div>

              {/* Description */}
              <div className="space-y-3 mb-6">
                <p className="text-[#9CA3AF] font-body text-sm">
                  Notifications are sent to the Telegram bot{" "}
                  <a
                    href="https://t.me/FractoLabsNotificationsBot"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#229ED9] hover:underline inline-flex items-center gap-1"
                  >
                    @FractoLabsNotificationsBot
                    <ExternalLink className="w-3 h-3" />
                  </a>.
                </p>
                <p className="text-[#9CA3AF] font-body text-sm">
                  Click <strong className="text-[#F3F4F6]">"Send Test Message"</strong> below. If you receive the test message, everything is working correctly.
                </p>
                <p className="text-[#9CA3AF] font-body text-sm">
                  If you don't receive it, try disconnecting the Telegram bot and reconnecting it again.
                </p>
              </div>

              {/* Result messages */}
              {testMessageResult === "success" && (
                <div className="mb-4 p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                  <p className="text-green-400 text-sm font-body flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" />
                    Test message sent successfully! Check your Telegram.
                  </p>
                </div>
              )}
              {testMessageResult === "error" && (
                <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                  <p className="text-red-400 text-sm font-body flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {testMessageError}
                  </p>
                </div>
              )}

              {/* Send Test Message Button */}
              <button
                onClick={handleSendTestMessage}
                disabled={isSendingTest}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-body font-bold transition-colors duration-200 bg-[#229ED9] text-white hover:bg-[#229ED9]/90 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="send-test-message-button"
              >
                {isSendingTest ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Test Message
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Disconnect Confirmation Modal */}
        {showDisconnectModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4" data-testid="disconnect-modal-overlay">
            <div className="bg-[#1A1D23] rounded-2xl border border-white/10 p-6 md:p-8 w-full max-w-md shadow-2xl relative animate-fade-in-up" data-testid="disconnect-modal">
              {/* Close button */}
              <button
                onClick={() => setShowDisconnectModal(false)}
                disabled={isDisconnecting}
                className="absolute top-4 right-4 text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors disabled:opacity-50"
                data-testid="disconnect-modal-close"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center flex-shrink-0">
                  <Unlink className="w-5 h-5 text-red-400" />
                </div>
                <h3 className="font-heading font-bold text-lg text-[#F3F4F6]">
                  Disconnect Telegram
                </h3>
              </div>

              {/* Description */}
              <p className="text-[#9CA3AF] font-body text-sm mb-6">
                Are you sure you want to disconnect Telegram notifications? You will stop receiving notifications via Telegram until you reconnect.
              </p>

              {/* Action Buttons */}
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowDisconnectModal(false)}
                  disabled={isDisconnecting}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-body font-medium transition-colors duration-200 bg-white/10 text-[#F3F4F6] hover:bg-white/15 border border-white/10 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="disconnect-cancel-button"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDisconnectTelegram}
                  disabled={isDisconnecting}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-body font-bold transition-colors duration-200 bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="disconnect-confirm-button"
                >
                  {isDisconnecting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Disconnecting...
                    </>
                  ) : (
                    <>
                      <Unlink className="w-4 h-4" />
                      Disconnect
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <footer className="py-6 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-6 mb-4">
              <a
                href="https://fractolabs.com/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#9CA3AF] text-sm hover:text-[#F3F4F6] transition-colors"
              >
                Privacy Policy
              </a>
              <span className="text-[#9CA3AF]/30">|</span>
              <a
                href="https://fractolabs.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#9CA3AF] text-sm hover:text-[#F3F4F6] transition-colors"
              >
                Terms of Service
              </a>
            </div>
            <p className="text-[#9CA3AF]/60 text-xs font-mono">
              © 2026 FractoLabs. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default Notifications;
